/* eslint-disable no-unused-expressions */
/**
 * @author Fernando Ruano
 * @file index.js
 * @description PageHeader Component
 */

// Dependencies
import React from 'react';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';
import stringSimilarity from 'string-similarity';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Select } from 'antd';
import moment from 'moment';

// Helpers
import {
  getStorageItem,
  setStorageItem
} from '@nephro/commons/helpers/storage-helper';
import { transformPatientName } from 'commons/helpers/utils';

// Components
import { pageTitlesStaff, pageTitlesPatient } from './titles';

const { Option } = Select;

const itemProps = {
  style: { color: 'white' }
};

const StyledSelect = styled(Select)`
  &.ant-select-single .ant-select-selector .ant-select-selection-item {
    color: white !important;
    font-size: 10px !important;
    font-weight: 700 !important;
  }
  &.ant-select {
    width: 200px;
    height: 25px !important;
    color: white;
  }

  @media (min-width: 680px) {
    &.ant-select {
      width: 350px;
      height: 25px !important;
    }
  }
`;

const profiles = {
  NEPHROLOGIST: 'Nefrólogo',
  NUTRITION: 'Nutrición',
  PSYCHOLOGY: 'Psicología',
  NURSING: 'Enfermería',
  MEDICALDIRECTOR: 'Director Médico',
  SOCIAL_WORK: 'Trabajo Social'
};

/**
 * PageHeader Component.
 * @param { Object } dashboardStaffActions - DashboardStaff Actions.
 * @return { Node } React PageHeader Component.
 */
function PageHeader({ dashboardStaffActions, actions, clinicId, userActions }) {
  const history = useHistory();
  const location = useLocation();
  const { t: translate } = useTranslation();
  const [clinicsData, setClinicsData] = React.useState([]);
  const [lastUpdateData, setLastUpdateData] = React.useState(null);
  const [isStaff, setIsStaff] = React.useState(false);

  const { getAdmonCodesByEntity, getLastUpdate } = dashboardStaffActions;
  const { getUserByUserName, getUserByEmail } = userActions;
  const { setClinicId } = actions;

  const userType = getStorageItem('isPatient');
  const userInfo = getStorageItem('user');

  const titlesByUserType = userType
    ? pageTitlesPatient(
        `${translate(
          'pageHeader.hello'
        )}, ${userInfo.first_name.toLowerCase()} ${userInfo.last_name.toLowerCase()}`
      )
    : pageTitlesStaff;

  let titleItem = '';

  const lastUpdateFormated = React.useMemo(() => {
    if (lastUpdateData === null) return '';
    return moment(lastUpdateData).locale('es').format('LLL') ?? '';
  }, [lastUpdateData]);

  titleItem = titlesByUserType.find((item) => {
    const includeDashboardDetail = item.path.includes('/dashboard-detail/');
    const listMainPath = item.path.split('/')[3];
    const listMainPathValidation =
      location.pathname.split('/')[3] === listMainPath;
    return includeDashboardDetail && listMainPathValidation;
  });

  if (!titleItem) {
    titleItem = titlesByUserType.find((itm) => {
      return (
        location.pathname === itm.path ||
        stringSimilarity.compareTwoStrings(location.pathname, itm.path) > 0.9
      );
    });
  }

  if (!titleItem) {
    const includeCategory = location.pathname.includes(
      '/main/educational-material/category/'
    );
    if (includeCategory) {
      const categorySplit = location.pathname.split('/');
      titleItem = {
        hasBack: true,
        path: '',
        subTitle: '',
        title: categorySplit[4]
      };
    }
  }

  if (!titleItem) {
    const includeCategory = location.pathname.includes(
      '/main/educational-material/element/'
    );
    if (includeCategory) {
      const elementSplit = location.pathname.split('/');
      titleItem = {
        hasBack: true,
        path: '',
        subTitle: '',
        title: elementSplit[5]
      };
    }
  }

  const onGetData = (_, resp) => {
    if (resp?.data) {
      setClinicsData(resp?.data);
    }
  };

  const onGetLastUpdateData = (_, resp) => {
    if (resp?.data) {
      setLastUpdateData(resp?.data?.last_versia_update);
    }
  };

  const isScheduleAppointmentsPath =
    location.pathname === '/main/scheduled-appointments' && isStaff;
  const clinicSelectCondition =
    location.pathname === '/main/dashboard' && isStaff;
  const patientNameCondition =
    location.pathname === '/main/dashboard' && !isStaff;

  const onGetUserData = async (_, resp) => {
    if (resp?.data) {
      const clinicData = resp?.data?.id_clinic;
      const isPatient = await getStorageItem('isPatient');
      const clinicIdStorage = await getStorageItem('clinicId');
      setIsStaff(!isPatient);
      const entityId = 2;
      if (clinicIdStorage === null) {
        setClinicId(clinicData);
        await setStorageItem('clinicId', clinicData);
      }
      if (clinicId === null) {
        setClinicId(clinicIdStorage);
      }
      getAdmonCodesByEntity({ entityId }, onGetData);
      getLastUpdate(onGetLastUpdateData);
    }
  };

  const onSelectClinic = (itm) => {
    setStorageItem('clinicId', itm);
    setClinicId(itm);
  };
  const patientInfo = getStorageItem('patientInfo');

  React.useEffect(() => {
    const getInitInfo = async () => {
      const userInf = await getStorageItem('user');
      const oktaEmailAuthenticated = await getStorageItem(
        'oktaEmailAuthenticated'
      );
      const useOkta = oktaEmailAuthenticated !== null;
      if (useOkta) {
        getUserByEmail({ email: oktaEmailAuthenticated }, onGetUserData);
      } else {
        await getUserByUserName(
          { username: userInf?.user_name },
          onGetUserData
        );
      }
    };
    getInitInfo();
  }, [translate, clinicId]);

  return titleItem ? (
    <div
      className={`ml-2 mr-7 ${
        !clinicSelectCondition && 'mt-2'
      } 'w-auto'  lxl:w-auto lg:mt-2 ms:mt-5 flex justify-between lg:flex iphone5:block items-center`}>
      <div className="flex flex-row items-center">
        {titleItem.hasBack && (
          <ArrowLeftOutlined
            className="mr-4 text-sm"
            {...itemProps}
            onClick={() => {
              history.goBack();
              if (titleItem?.backPosition) {
                setTimeout(() => {
                  const a = document.createElement('a');
                  a.href = titleItem.backPosition;
                  a.click();
                  a.remove();
                }, 100);
              }
            }}
          />
        )}
        <h2
          style={{ fontSize: 14 }}
          className={`text-base md:text-xl text-white font-bold mr-3 mb-0`}>
          {patientNameCondition
            ? transformPatientName(translate(titleItem.title))
            : translate(titleItem.title)}
        </h2>
        <h2 className="text-xxs md:text-xs text-white70 mt-2 mr-2">
          {`${translate(titleItem.subTitle)} ${
            lastUpdateFormated && clinicSelectCondition
              ? lastUpdateFormated
              : ''
          }`}
          {titleItem.title === 'dashboard.records' &&
            `${patientInfo.first_name} ${patientInfo.last_name}`}
        </h2>
      </div>
      {(clinicSelectCondition || isScheduleAppointmentsPath) && (
        <div>
          <StyledSelect
            value={clinicId}
            disabled={userInfo?.profile.name !== profiles.MEDICALDIRECTOR}
            onSelect={onSelectClinic}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            filterSort={(optionA, optionB) =>
              optionA.children
                .toLowerCase()
                .localeCompare(optionB.children.toLowerCase())
            }
            bordered={false}>
            <Option key={'null'} value={''}>
              Todas las clínicas
            </Option>
            {clinicsData?.length > 0 &&
              clinicsData?.map((itm, idx) => {
                return (
                  <Option key={idx} value={itm.codigo_id}>
                    {itm.nombre}
                  </Option>
                );
              })}
          </StyledSelect>
        </div>
      )}
    </div>
  ) : (
    <div />
  );
}

/**
 * @name PageHeader.propTypes
 * @type { Object }
 */
PageHeader.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getAdmonCodesByEntity: PropTypes.func.isRequired,
    getLastUpdate: PropTypes.func.isRequired
  }),
  actions: PropTypes.shape({
    setClinicId: PropTypes.func.isRequired
  }).isRequired,
  userActions: PropTypes.shape({
    getUserByUserName: PropTypes.func.isRequired,
    getUserByEmail: PropTypes.func.isRequired
  }).isRequired,
  clinicId: PropTypes.any
};

/**
 * Default props
 */
PageHeader.defaultProps = {};

export default PageHeader;
